import {
  AlertCircle,
  Archive,
  Check,
  CircleDashed,
  Clock3,
  Info,
  ShieldCheck,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react"

type BadgeTone = "neutral" | "information" | "success" | "warning" | "danger"

const statusMap: Record<string, { label: string; tone: BadgeTone; icon: LucideIcon }> = {
  new: { label: "New", tone: "information", icon: Info },
  needs_context: { label: "Needs context", tone: "warning", icon: Clock3 },
  ready: { label: "Ready", tone: "information", icon: Check },
  investigating: { label: "Investigating", tone: "information", icon: CircleDashed },
  needs_approval: { label: "Needs approval", tone: "warning", icon: ShieldCheck },
  action_running: { label: "Action running", tone: "information", icon: CircleDashed },
  resolved: { label: "Resolved", tone: "success", icon: Check },
  escalated: { label: "Escalated", tone: "neutral", icon: AlertCircle },
  failed: { label: "Failed", tone: "danger", icon: TriangleAlert },
  archived: { label: "Archived", tone: "neutral", icon: Archive },
  high: { label: "High", tone: "danger", icon: AlertCircle },
  urgent: { label: "Urgent", tone: "danger", icon: TriangleAlert },
  normal: { label: "Normal", tone: "neutral", icon: Info },
  low: { label: "Low", tone: "neutral", icon: Info },
  healthy: { label: "Healthy", tone: "success", icon: Check },
  degraded: { label: "Degraded", tone: "warning", icon: AlertCircle },
  disconnected: { label: "Disconnected", tone: "neutral", icon: AlertCircle },
  high_confidence: { label: "High confidence", tone: "success", icon: ShieldCheck },
  medium_confidence: { label: "Medium confidence", tone: "warning", icon: ShieldCheck },
  low_confidence: { label: "Low confidence", tone: "danger", icon: ShieldCheck },
  pending_confidence: { label: "Confidence pending", tone: "neutral", icon: CircleDashed },
  waiting: { label: "Waiting", tone: "warning", icon: Clock3 },
  approved: { label: "Approved", tone: "success", icon: Check },
  rejected: { label: "Rejected", tone: "danger", icon: AlertCircle },
  expired: { label: "Expired", tone: "neutral", icon: Clock3 },
  clarification_requested: { label: "Clarification requested", tone: "information", icon: Info },
  published: { label: "Published", tone: "success", icon: Check },
  draft: { label: "Draft", tone: "neutral", icon: CircleDashed },
  review: { label: "In review", tone: "information", icon: Clock3 },
  ready_for_review: { label: "Ready for review", tone: "information", icon: ShieldCheck },
  accepted: { label: "Accepted", tone: "success", icon: Check },
  changes_requested: { label: "Changes requested", tone: "warning", icon: Clock3 },
  candidate: { label: "Candidate", tone: "neutral", icon: CircleDashed },
  testing: { label: "Testing", tone: "information", icon: CircleDashed },
}

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  const config = statusMap[status] ?? {
    label: status.replaceAll("_", " "),
    tone: "neutral" as const,
    icon: Info,
  }
  const Icon = config.icon
  return (
    <span className={`status-badge status-${config.tone}`}>
      <Icon aria-hidden={true} />
      {label ?? config.label}
    </span>
  )
}
